import type * as React from 'react'
import {
  forwardRef,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { DndContext, DragOverlay } from '@dnd-kit/core'
import type {
  DndContextProps,
  DragCancelEvent,
  DragEndEvent,
  DraggableAttributes,
  DragOverlayProps,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  SortableContext as DndKitSortableContext,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type {
  SortableContextProps as DndKitSortableContextProps,
  UseSortableArguments,
} from '@dnd-kit/sortable'
import { clsx } from 'clsx'

import {
  GripHorizontalIcon,
  Tooltip,
  UnstyledButton,
  useMergeRefs,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { DROP_ANIMATION } from '~publish/helpers/dndkit/constants'
import { useCustomSensors } from '~publish/hooks/useCustomSensors'
import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import styles from './Sortable.module.css'

/**
 * Sortable - A composable drag-and-drop sorting component built on dnd-kit.
 *
 * Provides sensible defaults while exposing the full dnd-kit API for customization.
 *
 * @example Basic usage
 * ```tsx
 * const [items, setItems] = useState([
 *   { id: '1', name: 'Item 1' },
 *   { id: '2', name: 'Item 2' },
 *   { id: '3', name: 'Item 3' },
 * ])
 *
 * const handleReorder = (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
 *   setItems((items) => {
 *     const oldIndex = items.findIndex((item) => item.id === activeId)
 *     const newIndex = items.findIndex((item) => item.id === overId)
 *     return arrayMove(items, oldIndex, newIndex)
 *   })
 * }
 *
 * <Sortable onReorder={handleReorder}>
 *   <Sortable.List items={items}>
 *     {items.map((item) => (
 *       <Sortable.Item key={item.id} id={item.id}>
 *         <Sortable.DragHandle />
 *         {item.name}
 *       </Sortable.Item>
 *     ))}
 *   </Sortable.List>
 *   <Sortable.Overlay>
 *     {(activeId) => {
 *       const item = items.find((item) => item.id === activeId)
 *       return (
 *         <Sortable.Item id={activeId} dragOverlay>
 *           <Sortable.DragHandle />
 *           {item?.name}
 *         </Sortable.Item>
 *       )
 *     }}
 *   </Sortable.Overlay>
 * </Sortable>
 * ```
 *
 * @example With vertical sorting strategy
 * ```tsx
 * import { verticalListSortingStrategy } from '@dnd-kit/sortable'
 *
 * <Sortable onReorder={handleReorder}>
 *   <Sortable.List items={items} strategy={verticalListSortingStrategy}>
 *     {items.map((item) => (
 *       <Sortable.Item key={item.id} id={item.id}>
 *         <Sortable.DragHandle />
 *         {item.name}
 *       </Sortable.Item>
 *     ))}
 *   </Sortable.List>
 * </Sortable>
 * ```
 *
 * @example With custom modifiers
 * ```tsx
 * import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
 *
 * <Sortable onReorder={handleReorder} modifiers={[restrictToVerticalAxis]}>
 *   ...
 * </Sortable>
 * ```
 *
 * @example With accessibility announcements
 * ```tsx
 * <Sortable
 *   onReorder={handleReorder}
 *   accessibility={{
 *     announcements: {
 *       onDragStart: ({ active }) => `Picked up ${active.id}`,
 *       onDragOver: ({ active, over }) => over ? `Over ${over.id}` : '',
 *       onDragEnd: ({ active, over }) => over ? `Dropped on ${over.id}` : 'Cancelled',
 *       onDragCancel: () => 'Drag cancelled',
 *     },
 *   }}
 * >
 *   ...
 * </Sortable>
 * ```
 */

/**
 * Sortable Context
 */
type SortableContextValue = {
  activeId: UniqueIdentifier | null
  isDragging: boolean
}

const SortableContext = createContext<SortableContextValue>({
  activeId: null,
  isDragging: false,
})

const useSortableContext = (): SortableContextValue =>
  useContext(SortableContext)

/**
 * Sortable Root
 * Provides the DnD context and manages drag state.
 * Wraps dnd-kit's DndContext with sensible defaults while allowing full customization.
 */
type SortableRootProps = Omit<
  DndContextProps,
  'onDragStart' | 'onDragEnd' | 'onDragCancel'
> & {
  children: React.ReactNode
  /**
   * Simplified callback when drag ends with reorder info.
   * Called only when item is dropped over a different item.
   */
  onReorder?: (activeId: UniqueIdentifier, overId: UniqueIdentifier) => void
  /**
   * Callback when drag starts
   */
  onDragStart?: (event: DragStartEvent) => void
  /**
   * Callback when drag ends
   */
  onDragEnd?: (event: DragEndEvent) => void
  /**
   * Callback when drag is cancelled
   */
  onDragCancel?: (event: DragCancelEvent) => void
}

const SortableRoot = ({
  children,
  onReorder,
  onDragStart,
  onDragEnd,
  onDragCancel,
  sensors: sensorsProp,
  modifiers = [restrictToParentElement],
  ...props
}: SortableRootProps): React.ReactElement => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const defaultSensors = useCustomSensors()
  const sensors = sensorsProp ?? defaultSensors
  const { isDragging } = useSortableContext()
  useDraggingCursor(isDragging)

  const handleDragStart = useCallback(
    (event: DragStartEvent): void => {
      setActiveId(event.active.id)
      onDragStart?.(event)
    },
    [onDragStart],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      setActiveId(null)
      const { active, over } = event

      if (over && active.id !== over.id) {
        onReorder?.(active.id, over.id)
      }

      onDragEnd?.(event)
    },
    [onDragEnd, onReorder],
  )

  const handleDragCancel = useCallback(
    (event: DragCancelEvent): void => {
      setActiveId(null)
      onDragCancel?.(event)
    },
    [onDragCancel],
  )
  const contextValue = useMemo(
    () => ({ activeId, isDragging: activeId !== null }),
    [activeId],
  )

  return (
    <SortableContext.Provider value={contextValue}>
      <DndContext
        {...props}
        sensors={sensors}
        modifiers={modifiers}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
      </DndContext>
    </SortableContext.Provider>
  )
}

SortableRoot.displayName = 'Sortable'

/**
 * Sortable List
 * Container that wraps sortable items with SortableContext.
 * Separate from SortableRoot to allow multiple Sortable Lists for cross-container dragging.
 * Wraps dnd-kit's SortableContext while rendering an `<ol>` element.
 */
type SortableListElement = React.ElementRef<'ol'>
type SortableListProps = Omit<
  React.ComponentPropsWithoutRef<'ol'>,
  'children' | 'id'
> &
  Omit<DndKitSortableContextProps, 'children'> & {
    children: React.ReactNode
    /**
     * Layout orientation for the sortable list.
     * Automatically selects the appropriate sorting strategy if not explicitly provided.
     * @default 'horizontal'
     */
    orientation?: 'vertical' | 'horizontal'
  }

const SortableList = forwardRef<SortableListElement, SortableListProps>(
  (
    {
      className,
      items,
      strategy: strategyProp,
      disabled,
      id,
      children,
      orientation = 'horizontal',
      ...props
    },
    forwardedRef,
  ) => {
    const strategy =
      strategyProp ??
      (orientation === 'vertical'
        ? verticalListSortingStrategy
        : horizontalListSortingStrategy)

    return (
      <DndKitSortableContext
        items={items}
        strategy={strategy}
        disabled={disabled}
        id={id}
      >
        <ol
          ref={forwardedRef}
          className={clsx(
            styles.list,
            orientation === 'vertical' && styles.vertical,
            className,
          )}
          {...props}
        >
          {children}
        </ol>
      </DndKitSortableContext>
    )
  },
)

SortableList.displayName = 'Sortable.List'

/**
 * Sortable Context
 */
type SortableItemContextValue = {
  attributes: DraggableAttributes | null
  listeners: ReturnType<typeof useSortable>['listeners'] | undefined
  setActivatorNodeRef: (element: HTMLElement | null) => void
}

const SortableItemContext = createContext<SortableItemContextValue>({
  attributes: null,
  listeners: {},
  setActivatorNodeRef: () => {},
})

const useSortableItemContext = (): SortableItemContextValue =>
  useContext(SortableItemContext)

/**
 * Sortable Item
 * Individual draggable item with built-in drag handle.
 * Wraps dnd-kit's useSortable hook.
 */
type SortableItemElement = React.ElementRef<'li'>
type SortableItemProps = Omit<React.ComponentPropsWithoutRef<'li'>, 'id'> &
  Pick<UseSortableArguments, 'id' | 'disabled' | 'data'>

const SortableItem = forwardRef<SortableItemElement, SortableItemProps>(
  (
    { id, className, style, disabled, data, children, ...props },
    _forwardedRef,
  ) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
      setActivatorNodeRef,
    } = useSortable({
      id,
      disabled,
      data: {
        type: 'item',
        ...data,
      },
    })

    useDraggingCursor(isDragging)

    const contextValue = useMemo(
      () => ({ attributes, listeners, setActivatorNodeRef }),
      [attributes, listeners, setActivatorNodeRef],
    )

    // Don't transition the actively dragged item - only other items shifting out of the way
    // The DragOverlay handles the drop animation for the dragged item
    const computedStyles: React.CSSProperties &
      Record<string, string | number | undefined> = {
      transition: isDragging ? undefined : transition,
      '--translate-x': `${Math.round(transform?.x ?? 0)}px`,
      '--translate-y': `${Math.round(transform?.y ?? 0)}px`,
      '--scale-x': transform?.scaleX ?? 1,
      '--scale-y': transform?.scaleY ?? 1,
      ...style,
    }

    return (
      <SortableItemContext.Provider value={contextValue}>
        <li
          {...props}
          ref={setNodeRef}
          className={clsx(
            styles.item,
            isDragging && styles.dragging,
            className,
          )}
          style={computedStyles}
        >
          {isDragging && <SortableDestination />}
          {children}
        </li>
      </SortableItemContext.Provider>
    )
  },
)

SortableItem.displayName = 'Sortable.Item'

/**
 * Sortable Destination
 * Visual placeholder shown where an item will be dropped
 */
type SortableDestinationElement = React.ElementRef<'div'>
type SortableDestinationProps = React.ComponentPropsWithoutRef<'div'>

const SortableDestination = forwardRef<
  SortableDestinationElement,
  SortableDestinationProps
>(({ className, ...props }, forwardedRef) => (
  <div
    ref={forwardedRef}
    className={clsx(styles.destination, className)}
    {...props}
  />
))

SortableDestination.displayName = 'Sortable.Destination'

/**
 * Sortable Overlay
 * Portal for rendering the dragged item overlay.
 * Wraps dnd-kit's DragOverlay with portal rendering.
 */
type SortableOverlayProps = Omit<DragOverlayProps, 'children'> & {
  /**
   * Render function that receives the active drag ID
   */
  children: (activeId: UniqueIdentifier) => React.ReactNode
}

const SortableOverlay: React.FC<SortableOverlayProps> = ({
  children,
  dropAnimation = DROP_ANIMATION,
  className,
  ...props
}) => {
  const portalContainer =
    document.querySelector('#portals-container') ?? document.body
  const { activeId } = useSortableContext()

  return createPortal(
    <DragOverlay
      dropAnimation={dropAnimation}
      className={clsx(styles.dragOverlay, className)}
      {...props}
    >
      {activeId && children(activeId)}
    </DragOverlay>,
    portalContainer,
  )
}

SortableOverlay.displayName = 'Sortable.Overlay'

/**
 * Sortable DragHandle
 * Custom drag handle component for more control
 */
type SortableDragHandleElement = React.ElementRef<typeof UnstyledButton>
type SortableDragHandleProps = React.ComponentPropsWithoutRef<
  typeof UnstyledButton
> & {
  /**
   * Accessible label for the drag handle
   * @default "Drag to reorder"
   */
  label?: string
  /**
   * Tooltip for the drag handle
   */
  tooltip?: string
}

const SortableDragHandle = forwardRef<
  SortableDragHandleElement,
  SortableDragHandleProps
>(
  (
    { className, label = 'Drag to reorder', tooltip, children, ...props },
    forwardedRef,
  ) => {
    const { attributes, listeners, setActivatorNodeRef } =
      useSortableItemContext()

    const mergedRef = useMergeRefs(forwardedRef, setActivatorNodeRef)
    const dragHandle = (
      <UnstyledButton
        ref={mergedRef}
        className={clsx(styles.dragHandle, className)}
        data-dnd
        {...attributes}
        {...listeners}
        {...props}
      >
        <VisuallyHidden>{label}</VisuallyHidden>
        {children ?? <GripHorizontalIcon />}
      </UnstyledButton>
    )

    if (tooltip) {
      return (
        <Tooltip content={tooltip} side="top" delay={800}>
          {dragHandle}
        </Tooltip>
      )
    }

    return dragHandle
  },
)

SortableDragHandle.displayName = 'Sortable.DragHandle'

/**
 * Compose all components into a single export
 */
const SortableObject = Object.assign(SortableRoot, {
  List: SortableList,
  Item: SortableItem,
  Overlay: SortableOverlay,
  Destination: SortableDestination,
  DragHandle: SortableDragHandle,
})

export { SortableObject as Sortable, useSortableContext }

export type {
  SortableRootProps,
  SortableListProps,
  SortableItemProps,
  SortableOverlayProps,
  SortableDestinationProps,
  SortableDragHandleProps,
}
